/**
 * 風の洞察 (Kaze no Dōsatsu) Frontend Service
 * API client for the market intelligence dashboard
 */

import api from './api';

// ─── Types ──────────────────────────────────────────────

export interface KazeHealth {
  status: string;
  name: string;
  version: string;
  uptime_seconds: number;
  adapters_connected: number;
  events_processed: number;
  last_poll: string | null;
}

export interface RealitySnapshot {
  snapshot_id: string;
  timestamp: string;
  macro_regime: {
    state: string;
    confidence: number;
    trend_direction: string;
  };
  crypto_regime: {
    state: string;
    narrative_driver: string;
    crowding_level: number;
  };
  risk_posture: string;
  volatility_state: {
    vix_level: number;
    regime: string;
    term_structure: string;
  };
  liquidity_state: string;
  dominant_narratives: NarrativeState[];
  catalyst_proximity: CatalystEntry[];
}

export interface NarrativeState {
  narrative: string;
  name?: string;
  narrative_id?: string;
  strength: number;
  trend: string;
  price_confirmation: boolean;
  assets?: string[];
  assets_affected?: string[];
  first_detected?: string;
  last_updated?: string;
  event_count: number;
}

export interface CatalystEntry {
  event: string;
  date: string;
  severity: string;
  expected_impact: string;
  assets_affected: string[];
  narrative_tags: string[];
}

export interface EdgeCandidate {
  candidate_id: string;
  timestamp: string;
  source_event_ids: string[];
  asset: string;
  edge_type: string;
  kaze_score: number;
  narrative_context: string;
  consensus_state: string;
  asymmetry_description: string;
  catalyst_window: string;
  confidence: number;
  monitoring_horizon: string;
}

export interface KazeAlert {
  tier: number;
  summary: string;
  timestamp: string;
  channels: string[];
  acknowledged: boolean;
}

export interface TomiBridgeStatus {
  tomi_health: {
    status: string;
    engine?: string;
    version?: string;
  };
  signals_forwarded: TomiSignalRecord[];
  total_forwarded: number;
}

export interface TomiSignalRecord {
  kaze_event_id: string;
  kaze_score: number;
  kaze_class: string;
  tomi_action: string;
  tomi_dominion_score: number;
  tomi_signal_class: string;
  assets: string[];
  timestamp: string;
}

export interface NarrativesData {
  narratives: NarrativeState[] | Record<string, NarrativeState>;
  collisions: { a: string; b: string }[];
  divergence_candidates: { narrative: string; strength: number }[];
}

export interface IntelEvent {
  event_id: string;
  source: string;
  source_tier: string;
  headline: string;
  content: string;
  timestamp: string;
  asset_tags: string[];
  narrative_tags: string[];
  classification: string;
  kaze_score: number;
  scores: {
    novelty: number;
    market_impact: number;
    cross_asset: number;
    credibility: number;
    sentiment: number;
    game_theoretic: number;
    divergence: number;
  };
  game_theoretic_read?: {
    advantaged: string[];
    trapped: string[];
    signal_type: string;
  };
  tomi_escalation: boolean;
}

export interface LearningData {
  total_feedback_records: number;
  edge_realized_count: number;
  edge_hit_rate: string | number;
  false_positive_count: number;
  false_positive_rate: string | number;
  current_weights: Record<string, number>;
}

// ─── Sector & Screener Types ────────────────────────────

export interface SectorConsensus {
  sector: string;
  etf: string;
  consensus_score: number;
  consensus_label: 'STRONG_BUY' | 'BUY' | 'NEUTRAL' | 'SELL' | 'STRONG_SELL';
  rs_rank: number;
  dw_composite: number;
  trend_score: number;
  momentum_score: number;
  breadth_score: number;
  signal: string;
  price: number;
  change_pct: number;
  relative_strength_vs_spy: number;
  timestamp: string;
}

export interface RotationSignal {
  description: string;
  detail: string;
  assets: string[];
}

export interface TickerAnalysis {
  ticker: string;
  sector: string;
  k_score: number;
  k_class: 'ALPHA' | 'STRONG' | 'NEUTRAL' | 'WEAK' | 'AVOID';
  components: {
    technical_rs: number;
    momentum: number;
    trend: number;
    fundamental: number;
    volume: number;
    sector_tide: number;
  };
  pnf_signal: string;
  pnf_score: number;
  current_price: number;
  change_pct: number;
  pe_ratio: number | null;
  eps: number | null;
  market_cap: number;
  volume: number;
  avg_volume: number;
  fifty_two_week_high: number;
  fifty_two_week_low: number;
  entry_price: {
    entry: number | null;
    target: number | null;
    stop: number | null;
    invalidation: number | null;
    risk_reward: number | null;
    strategy: string;
    fib_levels?: Record<string, number>;
  };
  timestamp: string;
}

export interface ChartAnalysisResult {
  ticker: string;
  dominion_score: number;
  signal_class: string;
  action: string;
  position_size: number;
  entry_price: number;
  stop_loss: number | null;
  target: number | null;
  risk_reward: number | null;
  invalidation: number | null;
  abstention_reasons: string[];
  chart_context: {
    current_price: number;
    rsi: number;
    macd_signal: string;
    trend: string;
    volume_trend: string;
    support: number | null;
    resistance: number | null;
    fib_levels: Record<string, number>;
  };
  k_score: number;
  sector_consensus: string;
  components: Record<string, unknown>;
  timestamp: string;
  error?: string;
}

export interface YahooTop100Ticker {
  symbol: string;
  name: string;
  price: number;
  change_pct: number;
  volume: number;
  market_cap: number;
  sector: string;
  industry: string;
  pe_ratio: number | null;
  eps: number | null;
  fifty_two_week_high: number;
  fifty_two_week_low: number;
  avg_volume_3m: number;
  screener: string;
  timestamp: string;
}

// ─── API Calls ──────────────────────────────────────────

const kazeService = {
  /** Get Kaze engine health status */
  async getHealth(): Promise<KazeHealth> {
    const res = await api.get('/kaze/health');
    return res.data.data;
  },

  /** Get current reality snapshot */
  async getReality(): Promise<RealitySnapshot | null> {
    try {
      const res = await api.get('/kaze/reality');
      return res.data.data;
    } catch {
      return null;
    }
  },

  /** Get narrative heatmap */
  async getNarratives(): Promise<NarrativesData | null> {
    try {
      const res = await api.get('/kaze/narratives');
      return res.data.data;
    } catch {
      return null;
    }
  },

  /** Get edge candidates */
  async getEdges(): Promise<EdgeCandidate[]> {
    try {
      const res = await api.get('/kaze/edges');
      return res.data.data?.edge_candidates || [];
    } catch {
      return [];
    }
  },

  /** Get alerts with optional tier filter */
  async getAlerts(tier?: number, limit: number = 30): Promise<KazeAlert[]> {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (tier) params.append('tier', String(tier));
      const res = await api.get(`/kaze/alerts?${params}`);
      return res.data.data?.alerts || [];
    } catch {
      return [];
    }
  },

  /** Get full intelligence packet */
  async getPacket(): Promise<Record<string, unknown> | null> {
    try {
      const res = await api.get('/kaze/packet');
      return res.data.data;
    } catch {
      return null;
    }
  },

  /** Get human-readable briefing */
  async getBriefing(): Promise<string | null> {
    try {
      const res = await api.get('/kaze/briefing');
      return res.data.data?.briefing || null;
    } catch {
      return null;
    }
  },

  /** Get Tomi bridge status */
  async getTomiBridge(): Promise<TomiBridgeStatus> {
    try {
      const res = await api.get('/kaze/tomi-bridge');
      return res.data.data;
    } catch {
      return {
        tomi_health: { status: 'unreachable' },
        signals_forwarded: [],
        total_forwarded: 0,
      };
    }
  },

  /** Get learning summary */
  async getLearning(): Promise<LearningData | null> {
    try {
      const res = await api.get('/kaze/learning');
      return res.data.data;
    } catch {
      return null;
    }
  },

  /** Trigger a manual poll cycle */
  async triggerPoll(): Promise<{ events_ingested: number } | null> {
    try {
      const res = await api.post('/kaze/poll', {});
      return res.data.data;
    } catch {
      return null;
    }
  },

  /** Get recent scored events for the intel feed */
  async getEvents(limit: number = 50): Promise<IntelEvent[]> {
    try {
      const res = await api.get(`/kaze/events?limit=${limit}`);
      return res.data.data?.events || [];
    } catch {
      return [];
    }
  },

  /** Get sector consensus rankings (DW + Yahoo + Quant) */
  async getSectors(): Promise<{ sectors: SectorConsensus[]; rotation_signals: RotationSignal[] }> {
    try {
      const res = await api.get('/kaze/sectors');
      return res.data.data || { sectors: [], rotation_signals: [] };
    } catch {
      return { sectors: [], rotation_signals: [] };
    }
  },

  /** Get ticker screener with K-scores */
  async getScreener(params?: { sector?: string; k_class?: string; limit?: number }): Promise<{ tickers: TickerAnalysis[]; total: number }> {
    try {
      const qs = new URLSearchParams();
      if (params?.sector) qs.append('sector', params.sector);
      if (params?.k_class) qs.append('k_class', params.k_class);
      if (params?.limit) qs.append('limit', String(params.limit));
      const res = await api.get(`/kaze/screener?${qs}`);
      return res.data.data || { tickers: [], total: 0 };
    } catch {
      return { tickers: [], total: 0 };
    }
  },

  /** Get Yahoo Finance daily top 100 */
  async getTop100(): Promise<YahooTop100Ticker[]> {
    try {
      const res = await api.get('/kaze/top-100');
      return res.data.data?.tickers || [];
    } catch {
      return [];
    }
  },

  /** Get sector ETF performance */
  async getSectorPerformance(): Promise<Record<string, unknown>> {
    try {
      const res = await api.get('/kaze/sector-performance');
      return res.data.data?.sectors || {};
    } catch {
      return {};
    }
  },

  /** Request Tomi agentic chart analysis for a ticker */
  async requestChartAnalysis(ticker: string): Promise<ChartAnalysisResult | null> {
    try {
      const res = await api.post(`/kaze/chart-analysis?ticker=${encodeURIComponent(ticker)}`, {});
      return res.data.data || null;
    } catch {
      return null;
    }
  },

  /** Get recent chart analysis log */
  async getChartAnalysisLog(limit: number = 20): Promise<ChartAnalysisResult[]> {
    try {
      const res = await api.get(`/kaze/chart-analysis/log?limit=${limit}`);
      return res.data.data?.analyses || [];
    } catch {
      return [];
    }
  },
};

export interface AgentChatMessage {
  id: string;
  agent: 'tomi' | 'kaze' | 'user';
  content: string;
  timestamp: string;
}

const kazeServiceExtended = {
  ...kazeService,

  async getChat(): Promise<AgentChatMessage[]> {
    try {
      const res = await api.get('/kaze/chat');
      return res.data?.data || res.data || [];
    } catch {
      return [];
    }
  },

  async sendChat(message: string): Promise<AgentChatMessage[]> {
    const res = await api.post('/kaze/chat', { message });
    return res.data?.data || res.data || [];
  },

  async triggerChatExchange(initiator: 'tomi' | 'kaze' = 'tomi'): Promise<AgentChatMessage[]> {
    const res = await api.post('/kaze/chat', { initiator });
    return res.data?.data || res.data || [];
  },

  async clearChat(): Promise<void> {
    await api.delete('/kaze/chat');
  },
};

export default kazeServiceExtended;
